import React, { useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { GET_INVENTORY_USAGE_SUMMARY } from "./graphql";

function formatDate(d) {
  return d.toISOString().split("T")[0];
}

function defaultDateRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 6); // last 7 days, inclusive
  return { startDate: formatDate(start), endDate: formatDate(end) };
}

const sumBy = (rows, key) => rows.reduce((acc, r) => acc + (r[key] || 0), 0);

// A category's "available" pool (Initial + Assigned, in parts) split visually
// into what got Cut vs what's left - two proportional bars sharing the same
// total width, so the eye reads "this much came in" against "this much went
// out" at a glance, with no charting library involved.
const FlowBar = ({ label, value, total, colorClass }) => {
  const pct = total > 0 ? Math.min((value / total) * 100, 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <span className="w-16 shrink-0 text-[11px] font-medium text-gray-500">{label}</span>
      <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-gray-100">
        <div
          className={`h-full rounded-full ${colorClass}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

const StatTile = ({ label, value, sub, colorClass }) => (
  <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
    <p className="text-xs font-medium text-gray-500">{label}</p>
    <p className={`mt-2 text-2xl font-semibold ${colorClass}`}>{value}</p>
    {sub && <p className="mt-1 text-xs text-gray-400">{sub}</p>}
  </div>
);

const CategoryCard = ({ category }) => {
  const available = category.initialParts + category.assignedParts;
  const outflowTotal = category.cutParts + category.remainingParts;
  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <p className="font-semibold text-gray-900">{category.categoryName}</p>
          <p className="text-[11px] text-gray-400">
            {category.isScanRequired ? "Scan-required" : "Bulk / autocut"}
            {category.parts ? ` · ${category.parts} parts/unit` : ""}
          </p>
        </div>
        {(category.writtenOffQty > 0 || category.rejectedQty > 0) && (
          <span className="rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-600">
            {category.writtenOffQty > 0 && `${category.writtenOffQty} lost`}
            {category.writtenOffQty > 0 && category.rejectedQty > 0 && " · "}
            {category.rejectedQty > 0 && `${category.rejectedQty} rejected`}
          </span>
        )}
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2 text-center">
        <div>
          <p className="text-lg font-semibold text-gray-700">{category.initialQty}</p>
          <p className="text-[11px] text-gray-400">Initial</p>
        </div>
        <div>
          <p className="text-lg font-semibold text-blue-600">{category.assignedQty}</p>
          <p className="text-[11px] text-gray-400">Assigned</p>
        </div>
        <div>
          <p className="text-lg font-semibold text-amber-600">{category.cutParts}</p>
          <p className="text-[11px] text-gray-400">Cut (parts)</p>
        </div>
      </div>

      <div className="mt-4 space-y-2">
        <FlowBar label="In" value={available} total={available} colorClass="bg-gray-300" />
        <FlowBar label="Cut" value={category.cutParts} total={outflowTotal} colorClass="bg-amber-500" />
      </div>

      <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-2">
        <span className="text-[11px] text-gray-400">
          {category.cutOrderCount} order{category.cutOrderCount === 1 ? "" : "s"} in range
        </span>
        <span className="text-xs font-medium text-emerald-600">
          {category.remainingParts} parts remaining
        </span>
      </div>
    </div>
  );
};

const InventoryUsageDashboard = ({ employees }) => {
  const [{ startDate, endDate }, setDateRange] = useState(defaultDateRange());
  const [employeeId, setEmployeeId] = useState("ALL");
  const [sortBy, setSortBy] = useState("cutParts");

  const { data, loading, error } = useQuery(GET_INVENTORY_USAGE_SUMMARY, {
    variables: { startDate, endDate, employeeId: employeeId === "ALL" ? null : employeeId },
    fetchPolicy: "network-only",
  });

  const response = data?.getInventoryUsageSummary;
  const categories = response?.data?.categories || [];
  const employeeBreakdown = response?.data?.employees || [];

  const totals = useMemo(
    () => ({
      initialParts: sumBy(categories, "initialParts"),
      assignedParts: sumBy(categories, "assignedParts"),
      cutParts: sumBy(categories, "cutParts"),
      remainingParts: sumBy(categories, "remainingParts"),
      assignedQty: sumBy(categories, "assignedQty"),
    }),
    [categories],
  );

  const employeeRows = useMemo(() => {
    const rows = employeeBreakdown.map((emp) => ({
      employeeId: emp.employeeId,
      employeeName: emp.employeeName,
      initialParts: sumBy(emp.categories, "initialParts"),
      assignedParts: sumBy(emp.categories, "assignedParts"),
      cutParts: sumBy(emp.categories, "cutParts"),
      remainingParts: sumBy(emp.categories, "remainingParts"),
    }));
    return rows.sort((a, b) => b[sortBy] - a[sortBy]);
  }, [employeeBreakdown, sortBy]);

  const selectedEmployeeName = employeeId === "ALL"
    ? "All Employees"
    : employees.find((e) => e.id === employeeId)?.name || "Employee";

  return (
    <div className="space-y-6">
      {/* FILTERS */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:flex-wrap bg-white border border-gray-100 rounded-2xl shadow-sm p-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
          <input
            type="date"
            value={startDate}
            max={endDate}
            onChange={(e) => setDateRange((prev) => ({ ...prev, startDate: e.target.value }))}
            className="px-3 py-2 rounded-xl border border-gray-300 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
          <input
            type="date"
            value={endDate}
            min={startDate}
            max={formatDate(new Date())}
            onChange={(e) => setDateRange((prev) => ({ ...prev, endDate: e.target.value }))}
            className="px-3 py-2 rounded-xl border border-gray-300 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Employee</label>
          <select
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
            className="px-3 py-2 rounded-xl border border-gray-300 bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
          >
            <option value="ALL">All Employees (company-wide)</option>
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>{emp.name}</option>
            ))}
          </select>
        </div>
        <div className="sm:ml-auto text-xs text-gray-400">
          Showing: <span className="font-medium text-gray-600">{selectedEmployeeName}</span>
        </div>
      </div>

      {loading && (
        <div className="flex justify-center items-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-600" />
          <span className="ml-3 text-gray-600 text-sm">Loading...</span>
        </div>
      )}

      {(error || response?.status === "ERROR") && (
        <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-lg p-4">
          {error?.message || response?.message}
        </div>
      )}

      {!loading && !error && response?.status !== "ERROR" && (
        <>
          {/* TOTALS */}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatTile
              label="Initial stock"
              value={totals.initialParts}
              sub="parts, at range start"
              colorClass="text-gray-700"
            />
            <StatTile
              label="Assigned"
              value={totals.assignedParts}
              sub={`${totals.assignedQty} units given out`}
              colorClass="text-blue-600"
            />
            <StatTile
              label="Cut"
              value={totals.cutParts}
              sub="parts consumed in range"
              colorClass="text-amber-600"
            />
            <StatTile
              label="Remaining"
              value={totals.remainingParts}
              sub="parts left at range end"
              colorClass="text-emerald-600"
            />
          </div>

          {/* CATEGORY CARDS */}
          {categories.length === 0 ? (
            <div className="rounded-2xl border border-gray-100 bg-white p-8 text-center text-sm text-gray-400 shadow-sm">
              No inventory activity for this selection.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {categories.map((cat) => (
                <CategoryCard key={cat.categoryId} category={cat} />
              ))}
            </div>
          )}

          {/* PER-EMPLOYEE BREAKDOWN (company-wide view only) */}
          {employeeId === "ALL" && employeeRows.length > 0 && (
            <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
              <div className="flex items-center justify-between border-b border-gray-100 p-4">
                <p className="font-semibold text-gray-900">Breakdown by employee</p>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-2 py-1 rounded-lg border border-gray-300 bg-white text-xs focus:ring-2 focus:ring-blue-500 focus:outline-none"
                >
                  <option value="cutParts">Sort by cut</option>
                  <option value="assignedParts">Sort by assigned</option>
                  <option value="remainingParts">Sort by remaining</option>
                </select>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500">
                      <th className="px-4 py-2">Employee</th>
                      <th className="px-4 py-2 text-right">Initial</th>
                      <th className="px-4 py-2 text-right">Assigned</th>
                      <th className="px-4 py-2 text-right">Cut</th>
                      <th className="px-4 py-2 text-right">Remaining</th>
                      <th className="px-4 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {employeeRows.map((row) => (
                      <tr key={row.employeeId} className="border-t border-gray-50 hover:bg-gray-50">
                        <td className="px-4 py-2 font-medium text-gray-800">{row.employeeName}</td>
                        <td className="px-4 py-2 text-right text-gray-600">{row.initialParts}</td>
                        <td className="px-4 py-2 text-right text-blue-600">{row.assignedParts}</td>
                        <td className="px-4 py-2 text-right text-amber-600">{row.cutParts}</td>
                        <td className="px-4 py-2 text-right text-emerald-600">{row.remainingParts}</td>
                        <td className="px-4 py-2 text-right">
                          <button
                            type="button"
                            onClick={() => setEmployeeId(row.employeeId)}
                            className="text-xs font-medium text-blue-600 hover:underline"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {employeeId !== "ALL" && (
            <button
              type="button"
              onClick={() => setEmployeeId("ALL")}
              className="text-xs font-medium text-blue-600 hover:underline"
            >
              ← Back to company-wide view
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default InventoryUsageDashboard;
